import { useCallback } from 'react'
import { fetchExperienceSearchList } from 'actions/ExperienceActions'
import {
  EXP_CATEGORY_AIRPORT_LOUNGE_ID,
  EXP_CATEGORY_AIRPORT_TRANSFER_ID,
  EXP_CATEGORY_WATER_TRANSFER_ID,
} from 'constants/experience'
import useStandaloneExperienceSearch from 'hooks/Experiences/useExperienceSearch'
import { useAppDispatch } from 'hooks/reduxHooks'
import { getExperienceDurationString } from 'lib/experiences/experienceUtils'
import { getExperienceOfferPageURL } from 'lib/offer/offerPageURL'
import { useIntl } from 'react-intl'
import type { ExperienceSearchContext, ExperienceSearchFallbackPlace } from 'tripPlanner/api/types'
import type { DrawerExperienceSearchTile } from './ExperienceDetailView'

const EMPTY_EXPERIENCES: Array<DrawerExperienceSearchTile> = []

// Transfers and airport lounges are not browseable "things to do", and airport
// transfers in particular tend to rank first, so exclude them from the drawer's
// experience list (matches the exclusions used for the offer-page top lists).
const EXCLUDED_BROWSE_CATEGORY_IDS = new Set<number>([
  EXP_CATEGORY_AIRPORT_TRANSFER_ID,
  EXP_CATEGORY_WATER_TRANSFER_ID,
  EXP_CATEGORY_AIRPORT_LOUNGE_ID,
])

interface UseExperienceSearchResult {
  experiences: Array<DrawerExperienceSearchTile>
  searchContext?: ExperienceSearchContext
  isLoading: boolean
  isError: boolean
  refetch: () => void
}

export function isBrowseableExperience(experience: DrawerExperienceSearchTile): boolean {
  return !experience.categories?.some((category) => EXCLUDED_BROWSE_CATEGORY_IDS.has(category.id))
}

function mapStandaloneExperienceToDrawer(
  experience: App.ExperienceOffer,
  intl: ReturnType<typeof useIntl>,
): DrawerExperienceSearchTile {
  const latitude = experience.location.latitude
  const longitude = experience.location.longitude
  return {
    offerId: experience.id,
    name: experience.name,
    description: experience.copy.description ?? '',
    location: experience.location.city ?? experience.location.name ?? '',
    images: experience.images,
    rating: experience.rating?.score,
    reviewsTotal: experience.rating?.reviewsTotal,
    geoLocation: latitude !== undefined && longitude !== undefined ? { lat: latitude, lng: longitude } : undefined,
    deeplinkUrl: getExperienceOfferPageURL(experience),
    price: experience.price,
    value: experience.pricing.value,
    discount: experience.discount,
    memberPrice: experience.pricing.memberPrice,
    memberDiscountPercent: experience.pricing.memberDiscountPercentage,
    currencyCode: experience.pricing.memberPriceCurrency,
    duration: getExperienceDurationString(experience, intl),
    bookingType: experience.bookingType,
    categories: experience.categories.map(category => ({
      id: category.id,
      label: category.name,
      value: category.name,
    })),
    inclusions: [],
    isSoldOut: experience.status !== 'ONLINE',
    leExclusive: experience.leExclusive,
    bookByDate: experience.bookByDate,
    expirationDate: experience.expirationDate,
    copy: experience.copy,
    giftType: experience.giftType,
    productType: experience.productType,
  }
}

function mapStandaloneSearchContext({
  fallbackPlace,
  placeId,
  searchQuery,
  searchedPlaceId,
}: {
  fallbackPlace?: ExperienceSearchFallbackPlace
  placeId: string
  searchQuery: string
  searchedPlaceId?: string
}) {
  if (!fallbackPlace || fallbackPlace.placeId !== searchedPlaceId) return undefined

  return {
    requestedQuery: searchQuery,
    requestedPlace: {
      placeId,
      name: searchQuery,
    },
    searchedPlace: {
      placeId: fallbackPlace.placeId,
      name: fallbackPlace.name,
      type: fallbackPlace.type,
    },
    isFallbackPlace: true,
  } satisfies ExperienceSearchContext
}

interface UseExperienceSearchOptions {
  searchQuery: string
  placeId?: string
  fallbackPlace?: ExperienceSearchFallbackPlace
  fallbackPlaceIds?: Array<string>
  from?: string
  to?: string
  sortBy?: Experiences.SortTypes
  enabled: boolean
}

export default function useExperienceSearch({
  searchQuery,
  placeId,
  fallbackPlace,
  fallbackPlaceIds,
  from,
  to,
  sortBy,
  enabled,
}: UseExperienceSearchOptions): UseExperienceSearchResult {
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const [standaloneList, standaloneExperiences] = useStandaloneExperienceSearch({
    placeId,
    fallbackPlaceIds,
    limit: 10,
    sortBy,
    enabled: enabled && !!placeId,
    from,
    to,
  })
  let experiences = EMPTY_EXPERIENCES
  if (placeId) {
    experiences = standaloneExperiences
      .filter((experience): experience is App.ExperienceOffer => !!experience)
      .map(experience => mapStandaloneExperienceToDrawer(experience, intl))
      .filter(isBrowseableExperience)
  }
  const refetch = useCallback(() => {
    if (!placeId) return

    dispatch(fetchExperienceSearchList(placeId, {
      sortBy,
      from,
      to,
    }, { force: true }))
  }, [dispatch, from, placeId, sortBy, to])

  return {
    experiences,
    searchContext: placeId ? mapStandaloneSearchContext({
      fallbackPlace,
      placeId,
      searchQuery,
      searchedPlaceId: standaloneList.isFallbackPlace ? standaloneList.placeId : undefined,
    }) : undefined,
    isLoading: !!placeId && !!standaloneList.fetching,
    isError: !!placeId && !!standaloneList.error,
    refetch,
  }
}
